import { FaceDetection } from '@vladmandic/face-api';
import { RefObject, useEffect, useState } from 'react';

import type {
  AutoCapturePillPassthroughProps,
  ScannerHelpModalPassthroughProps,
  ScannerOverlayPassthroughProps,
} from 'inquiry/lib/types';
import BaseScanner from 'inquiry/scanners/BaseScanner';
import {
  DETECT_INTERVAL,
  GOVERNMENT_ID_BACK_TRIGGER_CONFIGS,
  GOVERNMENT_ID_TRIGGER_CONFIGS,
  GovernmentIdTriggerParams,
  GovernmentIdTriggerType,
} from 'inquiry/scanners/GovernmentIdScanner/client';
import { FaceApiClient } from 'lib/clients/FaceApiClient';
import FrameTrigger from 'lib/clients/FrameTrigger';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient from 'lib/clients/LivePhotoCaptureClient';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import useInquiryFeatureFlag from 'lib/hooks/useInquiryFeatureFlag';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { ButtonComponentStyles } from 'lib/interfaces/ui-components';
import { GovernmentIdOverlay, GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { calculateGlareRatio, calculateLuminosity, Rect } from 'verify/lib/utils/image';
import { bufferImageWithOverlayWindow } from 'verify/lib/utils/webcam';
import { CaptureMethod } from 'verify/models/verification';
import { SelectedCamera } from 'verify/scanners/GovernmentIdMicroblinkScanner/MicroblinkCameraUtils';

const FACE_DETECTION_THRESHOLD = 0.3;

interface GovernmentIdScannerProps {
  autoCaptureEnabled: boolean;
  setAutoCaptureReady: () => void;
  delayUntilManualCaptureMs?: number;
  handleCapture: (
    imageDataUris: string[],
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData,
  ) => Promise<void>;
  handleError: (err: Error | string, state: 'load' | 'capture') => void;
  isPassport: boolean;
  labelTakePhoto: string;
  renderOverlay: (props: ScannerOverlayPassthroughProps) => JSX.Element;
  videoConstraints: MediaTrackConstraints;
  requiresSide: GovernmentIdSide;
  handleTrigger: (
    trigger: FrameTrigger<
      unknown,
      unknown,
      StepGovernmentIdConfig['localizations']['capture-page']
    >,
  ) => void;
  handleCameraChange: (selectedCamera: SelectedCamera) => void;
  selectedCamera?: string;
  cameraSelectOptions: SelectedCamera[];
  shouldDisplayCameraSelector: boolean;
  overlayReady: boolean;
  overlayRef: RefObject<HTMLDivElement>;
  overlayCaptureWindowRef: RefObject<HTMLDivElement>;
  previewViewfinderRef: RefObject<HTMLDivElement>;
  submitButtonStyles?: ButtonComponentStyles;
  showHelpButton?: boolean;
  helpButtonIconColor?: string;
  helpButtonLabel?: string;
  enableGlareDetection?: boolean;
  renderAutoCapturePill?: (props: AutoCapturePillPassthroughProps) => JSX.Element;
  renderHelpModal?: (props: ScannerHelpModalPassthroughProps) => JSX.Element;
  uiReady: boolean;
  videoReady: boolean;

  // props (in addition to requiresSide) to pass to BaseScanner to render overlay inline
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  governmentIdClass: GovernmentIdClass;
  captureOverlay?: GovernmentIdOverlay | null;

  captureFrameLabel: string;
  hint?: string | null;
  prompt?: string | null;
}

/**
 * GovernmentIdScanner is for when we want to capture NOT using Microblink
 * However, there may be Microblink usage to extract text from the captured still image
 * for autoclassification if the config is enabled. We don't want to use Microblink autocapture
 * for autoclassification because through testing, it has been quite unreliable, and we don't
 * need Microblink to recognize the ID since we have Scanner to do that for us. Hence, we use a
 * simpler autocapture and take a best-effort approach in extracting the text from the captured
 * image using Microblink.
 */
const GovernmentIdScanner = ({
  /**
   * Manual capture was always enabled for GovernmentIdScanner before this prop was implemented.
   */
  delayUntilManualCaptureMs = 0,
  autoCaptureEnabled,
  setAutoCaptureReady,
  handleCapture,
  handleError,
  isPassport,
  labelTakePhoto,
  renderOverlay,
  videoConstraints,
  handleTrigger,
  handleCameraChange,
  selectedCamera,
  cameraSelectOptions,
  shouldDisplayCameraSelector,
  overlayReady,
  overlayRef,
  overlayCaptureWindowRef,
  previewViewfinderRef,
  submitButtonStyles,
  showHelpButton,
  helpButtonIconColor,
  helpButtonLabel,
  enableGlareDetection,
  renderAutoCapturePill,
  renderHelpModal,
  uiReady,
  videoReady,
  // props to pass to BaseScanner to render overlay inline
  assets,
  governmentIdClass,
  requiresSide,
  captureOverlay,
  captureFrameLabel,
  hint,
  prompt,
}: GovernmentIdScannerProps) => {
  // If errored, stops attempting face detection / autocapture entirely.
  const [errored, setErrored] = useState(false);

  const enableLowLightDetectionWeb = useInquiryFeatureFlag(
    'inquiry:enable-low-light-detection-web',
  );
  const disableGovIdFaceApi = useInquiryFeatureFlag('inquiry:disable-gov-id-face-api');

  useEffect(() => {
    if (disableGovIdFaceApi) return;

    try {
      FaceApiClient.loadModels().then(setAutoCaptureReady);
    } catch {
      setErrored(true);
    }
  }, []);

  const paramsFromVideoFrame = async (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    _captureClient: LivePhotoCaptureClient | null,
    triggerClient: FrameTriggerClient<
      GovernmentIdTriggerType,
      GovernmentIdTriggerParams,
      StepGovernmentIdConfig['localizations']['capture-page']
    >,
    viewfinderRegionInCanvas: Rect,
  ): Promise<GovernmentIdTriggerParams> => {
    let detection: FaceDetection[] = [];
    if (!errored && FaceApiClient.isLoaded() && FaceApiClient.isMediaLoaded(videoElement)) {
      bufferImageWithOverlayWindow({
        source: videoElement,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        overlayCaptureWindowElement: overlayCaptureWindowRef.current,
        canvasBuffer,
      });

      try {
        detection = await FaceApiClient.detectFaces(canvasBuffer, {
          scoreThreshold: FACE_DETECTION_THRESHOLD,
        });
      } catch (err) {
        // NOTE: Some Android devices fail to compile the fragment shader.
        // If many devices hit this, consider following recommendation here:
        // https://github.com/tensorflow/tfjs/issues/952

        // Here, we stop tick'ing but do nothing else because they can manually take a photo.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleError(err, 'capture');
        setErrored(true);
      }
    }

    let faceBoundingBox;
    if (detection.length === 1) {
      // Normalize bounding box
      faceBoundingBox = detection[0].box.rescale({
        width: 1 / canvasBuffer.width,
        height: 1 / canvasBuffer.height,
      });
    }

    return {
      currentTrigger: triggerClient.getTriggered().getType(),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      faceBoundingBox,
      glareRatio: enableGlareDetection ? calculateGlareRatio(canvasBuffer) : 0,
      luminosityData: enableLowLightDetectionWeb
        ? calculateLuminosity(canvasBuffer, viewfinderRegionInCanvas)
        : null,
      isPassport: isPassport,
    };
  };
  const shouldSubmit = (
    _params: GovernmentIdTriggerParams,
    triggerClient: FrameTriggerClient<
      GovernmentIdTriggerType,
      GovernmentIdTriggerParams,
      StepGovernmentIdConfig['localizations']['capture-page']
    >,
  ) => {
    return triggerClient.getTriggered().getType() === GovernmentIdTriggerType.Capture;
  };

  return (
    <BaseScanner<
      GovernmentIdTriggerType,
      GovernmentIdTriggerParams,
      StepGovernmentIdConfig['localizations']['capture-page']
    >
      handleCapture={({ imageSrcs, captureMethod, cameraInspectionData }) =>
        handleCapture(imageSrcs, captureMethod, cameraInspectionData)
      }
      handleFailed={handleError}
      detectInterval={DETECT_INTERVAL}
      labelTakePhoto={labelTakePhoto}
      videoConstraints={videoConstraints}
      // don't autocapture back / barcode / passport signature since GOVERNMENT_ID_TRIGGER_CONFIGS looks for faceBoundingBox
      triggerConfigs={
        autoCaptureEnabled ? GOVERNMENT_ID_TRIGGER_CONFIGS : GOVERNMENT_ID_BACK_TRIGGER_CONFIGS
      }
      paramsFromVideoFrame={paramsFromVideoFrame}
      shouldSubmit={shouldSubmit}
      hasValidQuad={(params) => params.currentTrigger === GovernmentIdTriggerType.HoldStill}
      renderOverlay={renderOverlay}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleTrigger={handleTrigger}
      delayUntilManualCaptureMs={autoCaptureEnabled ? delayUntilManualCaptureMs : 0}
      shouldDisplayCameraSelector={shouldDisplayCameraSelector}
      cameraSelectOptions={cameraSelectOptions}
      selectedCamera={selectedCamera}
      handleCameraChange={handleCameraChange}
      overlayReady={overlayReady}
      overlayRef={overlayRef}
      overlayCaptureWindowRef={overlayCaptureWindowRef}
      previewViewfinderRef={previewViewfinderRef}
      submitButtonStyles={submitButtonStyles}
      showHelpButton={showHelpButton}
      helpButtonIconColor={helpButtonIconColor}
      helpButtonLabel={helpButtonLabel}
      renderAutoCapturePill={renderAutoCapturePill}
      renderHelpModal={renderHelpModal}
      scannerType='governmentId'
      uiReady={uiReady}
      videoReady={videoReady}
      // props (in addition to requiresSide) to pass to BaseScanner to render overlay inline
      assets={assets}
      governmentIdClass={governmentIdClass}
      captureOverlay={captureOverlay}
      captureFrameLabel={captureFrameLabel}
      hint={hint}
      prompt={prompt}
      requiresSide={requiresSide}
    />
  );
};

export default GovernmentIdScanner;
